import type { FeatureFlagDefinition } from '../types/featureFlags';

export const FEATURE_FLAGS: FeatureFlagDefinition[] = [
  {
    id: 'tools.showCalibreMissingManufacturerIdAudit',
    label: '"Audit calibres missing manufacturerId" button (Movement Manufacturers)',
    description:
      'Surfaces a read-only audit showing which manufacturer strings appear on calibres that have no manufacturerId FK. Use to find entities/synonyms to add before re-running the backfill.',
    category: 'maintenance',
    defaultValue: false,
  },
  {
    id: 'tools.showRefsUnresolvedCalibreAudit',
    label: '"Audit refs with unresolved calibre" button (Movement Manufacturers)',
    description:
      'Surfaces a read-only audit showing calibre strings used by refs that do not resolve to any entry in custom_calibres. Useful after a backfill dry-run reports a high refsSkippedNoManufacturerId.',
    category: 'maintenance',
    defaultValue: false,
  },
  {
    id: 'tools.showCalibresUnresolvedBrandIdAudit',
    label: '"Audit calibres with unresolved brandId" button (Movement Manufacturers)',
    description:
      'Surfaces a read-only audit of custom_calibres docs whose brandId is empty AND brandName is non-empty (i.e. legacy free-text values the backfill could not resolve). Use to map / clear them by hand.',
    category: 'maintenance',
    defaultValue: false,
  },
  {
    id: 'tools.showAkaBrandIdShapeAudit',
    label: '"Audit AKA brandId shape" button (Movement Manufacturers)',
    description:
      'Surfaces a client-side audit/migration tool for calibre aliasDetails[].brandId values that are legacy free-text (e.g. "Bell & Ross") instead of a watchBrands slug. Proposes slug replacements, applies the auto-fixable ones, and offers a follow-up Algolia resync.',
    category: 'maintenance',
    defaultValue: true,
  },
  {
    id: 'tools.showPushAlgoliaSettings',
    label: '"Push Algolia Index Settings" button (Movement Manufacturers)',
    description:
      'Surfaces the callable that declares facetable attributes on the Algolia refs index. Idempotent — safe to re-run after deploying functions that add new denormalized fields.',
    category: 'maintenance',
    defaultValue: false,
  },
  {
    id: 'tools.showResyncToAlgolia',
    label: '"Resync to Algolia" button (Movement Manufacturers)',
    description:
      'Surfaces the unified Resync to Algolia panel: pick any of References / Brands / Manufacturers / Calibres, each with an optional full-rebuild (wipe-first) toggle. Incremental requires typing RESYNC; any full rebuild escalates to FULL REBUILD.',
    category: 'maintenance',
    defaultValue: false,
  },
  {
    id: 'tools.showEntityIndexTools',
    label: 'Entity Algolia index settings (brands / manufacturers / calibres)',
    description:
      'Surfaces the settings-push buttons for the watchkeeper_brands / watchkeeper_manufacturers / watchkeeper_calibres indexes. Idempotent — safe to re-run. Backfills now live in the unified Resync to Algolia panel.',
    category: 'maintenance',
    defaultValue: true,
  },
  {
    id: 'tools.showManufacturerDataHealth',
    label: '"Data Health Overview" button (Movement Manufacturers)',
    description:
      'Surfaces the read-only Phase 2 cross-manufacturer Data Health audit: manual vs ref-derived brand links, suggested primary brand and isGeneric classification, sortable by diff count. Purely diagnostic — no writes. Per-manufacturer detail also gains a Data Health section regardless of this flag.',
    category: 'maintenance',
    defaultValue: true,
  },
  {
    id: 'tools.showRepropagateBrandToRefs',
    label: '"Re-propagate to References" button (Brand Editor)',
    description:
      'Surfaces a maintenance button on the brand editor that re-runs the brand→refs propagation sweep (touch-updatedAt on every ref under the brand). Use when an automatic propagation appears stuck or pre-dates a code change. Idempotent and safe.',
    category: 'maintenance',
    defaultValue: false,
  },
  {
    id: 'tools.extractorJobs',
    label: '"Run scraper" button (Brand Editor) + Extractor Jobs tab',
    description:
      'Surfaces the admin-triggered extractor: a Run scraper button on brands a registered module can crawl, and the cross-brand Extractor Jobs history under Catalogue. Off during rollout because the extractor runs on a machine that is not always on, and a button that intermittently fails is worse than no button. Configure the service under Configuration → Extractor Service (that screen is always visible, so the URL and secret can be set before this is switched on).',
    category: 'experimental',
    defaultValue: false,
  },
  {
    id: 'calibre.dualWriteLegacyMovement',
    label: 'Dual-write legacy movement fields alongside calibreId',
    description:
      'WRITE-PATH flag, not a UI gate. While ON, admin flows that link a reference to a calibre keep populating the legacy `movement` blob, `movementType` and `functions` from the calibre spec, in addition to the authoritative `calibreId`. Those three are what the server-built `calibreCache` replaces, so turning this OFF is the calibres-as-modules cutover for movement specs. Do not switch it off until every reader prefers `calibreCache` — refs written while it is off carry no legacy movement values to fall back on.',
    category: 'destructive',
    defaultValue: true,
  },
  {
    id: 'calibre.dualWriteLegacyCalibreString',
    label: 'Dual-write the legacy calibre string alongside calibreId',
    description:
      'WRITE-PATH flag, not a UI gate. While ON, admin flows that link a reference to a calibre keep writing the free-text `calibre` field. Turning this OFF drops the string entirely — a bulk calibre update then writes nothing but the `calibreId` link, and aborts outright when the typed value resolves to no calibre. Do not switch it off until every reader resolves the calibre through `calibreId`.',
    category: 'destructive',
    defaultValue: true,
  },
  {
    id: 'notifications.newPreferencesShape',
    label: 'Read the per-feature × per-channel notification preference matrix',
    description:
      'READ-PATH flag, not a UI gate. While OFF, the notification producers resolve preferences from the legacy flat flags at users/{uid}/preferences/rotationNotificationPreferences. While ON, they prefer the matrix at users/{uid}.notificationPreferences, falling back to the flat flag PER FEATURE for anything the matrix does not yet cover — so switching it on before the backfill has finished is survivable, just slower (an extra subcollection read per user per sweep). Recommended order: run migrateNotificationPreferences dry, then for real until nextCursor is null, confirm the counts, then switch this on. Defaults OFF, and a failed read of this document is also treated as OFF, because the legacy path is the existing behaviour and a transient error must not change what anybody receives.',
    category: 'destructive',
    defaultValue: false,
  },
];

export function getFeatureFlagDefinition(
  id: string,
): FeatureFlagDefinition | undefined {
  return FEATURE_FLAGS.find((f) => f.id === id);
}
